import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import AuthHero from "../../components/auth/AuthHero";
import AuthMobileLogo from "../../components/auth/AuthMobileLogo";
import LanguageSelector from "../../components/common/LanguageSelector";
import { authService } from "../../services/api";
import { resetSignup } from "../../store/slices/signupSlice";

const SignIn = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [verificationCode, setVerificationCode] = useState([
    "",
    "",
    "",
    "",
    "",
    "",
  ]);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [isInvalidMail, setIsInvalidMail] = useState(false);
  const [isInvalidPassword, setIsInvalidPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("login");
  // 'sms' | 'email' | null — which resend channel is currently in flight.
  // We track per-channel so only the clicked button shows "Sending..." instead
  // of both buttons going gray.
  const [resending, setResending] = useState(null);
  const [invitationToken, setInvitationToken] = useState(null);
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  // Trust this device for 14 days — opt-in, default false. Sent to backend
  // on OTP verify; backend mints a TrustedDevice + HttpOnly cookie. Next
  // login from this browser skips the OTP step entirely.
  const [trustDevice, setTrustDevice] = useState(false);
  const [isOtpError, setIsOtpError] = useState(false);

  // Handle invitation parameters and authentication check
  useEffect(() => {
    console.log(
      "SignIn - useEffect running, initialCheckDone:",
      initialCheckDone
    );

    // Only run this once to avoid loops
    if (initialCheckDone) return;

    const searchParams = new URLSearchParams(location.search);
    const rawToken =
      searchParams.get("token") || searchParams.get("invite_token");
    const emailParam = searchParams.get("email");

    // Fix email '+' normalization similar to SignUp
    let email = emailParam;
    if (emailParam) {
      if (emailParam.includes(" ") && emailParam.includes("@")) {
        const beforeAt = emailParam.split("@")[0];
        const afterAt = emailParam.split("@")[1];
        if (beforeAt && afterAt && beforeAt.includes(" ")) {
          const fixedBeforeAt = beforeAt.replace(/\s+/g, "+");
          email = `${fixedBeforeAt}@${afterAt}`;
        }
      }
    }

    console.log("SignIn - Current URL:", window.location.href);
    console.log("SignIn - Auth state:", {
      hasTokens: !!auth.tokens?.access,
      hasUser: !!auth.user,
      userHasRoles: auth.user?.roles?.length > 0,
    });

    setInitialCheckDone(true);

    // If this is an invitation flow (has token and email)
    if (rawToken && email) {
      console.log("Processing invitation parameters");
      setInvitationToken(rawToken);
      setFormData((prev) => ({ ...prev, email }));

      // If user is already authenticated and has roles, redirect to home to show pending invites
      if (
        auth.tokens?.access &&
        auth.user &&
        auth.user.roles &&
        auth.user.roles.length > 0
      ) {
        console.log(
          "User already authenticated with invitation, redirecting to home"
        );
        toast("You have a pending invitation. Please check your dashboard.", {
          duration: 5000,
          style: {
            background: "#f0f9ff",
            border: "1px solid #0ea5e9",
            color: "#0c4a6e",
          },
        });
        navigate("/", { replace: true });
        return;
      }

      // Show invitation message for login
      if (!auth.tokens?.access) {
        toast("Please log in to accept your invitation.", {
          duration: 5000,
          style: {
            background: "#f0f9ff",
            border: "1px solid #0ea5e9",
            color: "#0c4a6e",
          },
        });
      }
    } else if (
      auth.tokens?.access &&
      auth.user &&
      auth.user.roles &&
      auth.user.roles.length > 0
    ) {
      // User is authenticated AND has roles, redirect to home
      console.log("User already authenticated with roles, redirecting to home");
      navigate("/", { replace: true });
      return;
    }

    console.log("SignIn - Initial check completed without redirect");
  // Intentionally run once on mount; dependencies omitted by design
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading) return;

    setError(null);
    setIsInvalidMail(false);
    setIsInvalidPassword(false);
    setLoading(true);

    try {
      const response = await authService.login({
        email: formData.email,
        password: formData.password,
        invite_token: invitationToken || null,
      });

      if (response?.success) {
        // Trusted-device fast path: backend already returned tokens, we
        // skip the 2fa step and go straight to the post-login redirect.
        // response.data here is the full userData (Redux-shape) from
        // _handleAuthSuccessPayload inside authService.
        if (response.otp_required === false && response.data?.user) {
          dispatch(resetSignup());
          redirectAfterAuth(response.data.user);
          return;
        }
        // Standard path: OTP was sent, switch to the 6-box code UI.
        setStep("2fa");
      } else {
        if (response?.error === "invalid_email") {
          setIsInvalidMail(true);
        } else if (response?.error === "invalid_password") {
          setIsInvalidPassword(true);
        }
        setError(response?.error || "Invalid credentials");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Shared post-auth redirect. Used by both the OTP-success path and the
  // trusted-device fast path so the navigation rules stay in one place.
  const redirectAfterAuth = (loggedInUser) => {
    const userHasRoles = Array.isArray(loggedInUser?.roles) && loggedInUser.roles.length > 0;
    // One-time notifications warmup after every successful login.
    window.dispatchEvent(
      new CustomEvent("notifications:warmup", { detail: { durationMs: 10000 } })
    );
    if (invitationToken) {
      toast.success("Login successful! Please check your pending invitations.", {
        duration: 4000,
      });
      navigate("/");
    } else if (userHasRoles) {
      navigate("/");
    } else {
      navigate("/onboarding");
    }
  };

  const handleCodeChange = (index, value) => {
    // If the user pasted (or typed too many chars at once), distribute the
    // digits across boxes starting from the current index.
    const digits = (value || "").replace(/\D/g, "");
    if (digits.length === 0) {
      const newCode = [...verificationCode];
      newCode[index] = "";
      setVerificationCode(newCode);
      return;
    }
    if (digits.length === 1) {
      const newCode = [...verificationCode];
      newCode[index] = digits;
      setVerificationCode(newCode);
      if (index < 5) document.getElementById(`code-${index + 1}`)?.focus();
      return;
    }
    // Multi-digit (typically a 6-digit paste)
    const newCode = ["", "", "", "", "", ""];
    // Preserve any existing digits BEFORE the index where paste started
    for (let i = 0; i < index; i += 1) newCode[i] = verificationCode[i] || "";
    let cursor = index;
    for (let i = 0; i < digits.length && cursor < 6; i += 1, cursor += 1) {
      newCode[cursor] = digits[i];
    }
    setVerificationCode(newCode);
    const lastFilled = Math.min(cursor, 5);
    document.getElementById(`code-${lastFilled}`)?.focus();
  };

  const handleCodePaste = (e) => {
    const text = (e.clipboardData?.getData("text") || "").replace(/\D/g, "");
    if (!text) return;
    e.preventDefault();
    handleCodeChange(0, text);
  };

  const handle2FASubmit = async (e) => {
    e?.preventDefault();
    if (loading) return;

    const code = verificationCode.join("");
    if (code.length !== 6) return;

    setError(null);
    setIsOtpError(false);
    setLoading(true);

    try {
      const response = await authService.verify2FA({
        email: formData.email,
        code,
        invite_token: invitationToken || null,
        // When the checkbox under the OTP box is ticked, backend creates
        // a TrustedDevice row + sets the HttpOnly cookie so the next
        // login from this browser can skip OTP for 14 days.
        trust_device: trustDevice,
      });

      if (response.success && response.data) {
        dispatch(resetSignup());
        redirectAfterAuth(response.data.user);
      } else {
        setError(response.error || "Invalid verification code");
        if (response?.error === "Invalid OTP.") {
          setIsOtpError(true);
        }
        setVerificationCode(["", "", "", "", "", ""]);
        // After clearing, return focus to the first box so the user can
        // immediately start typing again instead of being stuck on the last input.
        setTimeout(() => document.getElementById("code-0")?.focus(), 0);
      }
    } catch (err) {
      console.error("2FA verification error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resending) return;

    setResending("sms");
    setError(null);
    setIsOtpError(false);

    try {
      const response = await authService.login({
        email: formData.email,
        password: formData.password,
        invite_token: invitationToken || null,
      });

      if (response?.success) {
        setVerificationCode(["", "", "", "", "", ""]);
        document.getElementById("code-0")?.focus();
      } else {
        setError(response?.error || "Failed to resend code");
      }
    } catch (err) {
      console.error("Resend code error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setResending(null);
    }
  };

  // Email-channel fallback: triggers when user can't receive SMS (some carriers / countries).
  const handleResendByEmail = async () => {
    if (resending) return;
    setResending("email");
    setError(null);
    setIsOtpError(false);
    try {
      const response = await authService.resendOtpByEmail({
        email: formData.email,
      });
      if (response?.success) {
        setVerificationCode(["", "", "", "", "", ""]);
        document.getElementById("code-0")?.focus();
      } else {
        setError(response?.error || "Failed to resend code via email");
      }
    } catch (err) {
      console.error("Resend by email error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setResending(null);
    }
  };

  useEffect(() => {
    console.log(step);
  }, [step]);

  return (
    <div className="flex min-h-screen flex-col">
      <div className="flex flex-1">
        <AuthHero pageName={"signin"} />
        {/* max-md:pt-28 reserves the band the centred mobile wordmark occupies
            (absolute, top-16 + h-12 = 112px) so the vertically centred form
            can never slide up underneath it on short viewports; max-md:pb-24
            keeps the form clear of the fixed support FAB bottom-right. */}
        <div className="flex-1 bg-white flex items-center justify-center relative max-md:pt-28 max-md:pb-24">
          {/* Mobile-only wordmark — the desktop logo sits in AuthHero's dark
              panel, which is hidden below md. */}
          <AuthMobileLogo />
          {/* Language Selector - Top Right */}
          <div className="absolute top-4 right-4 z-10">
          <LanguageSelector isFromAuth={true} />
          </div>

          <div className="w-full max-w-xl px-5">
            <div className="mb-[100px] md:mb-0">
              <h2 className="text-[23.3px] leading-[28px] font-semibold text-[#111927] mb-2">
                Log in
              </h2>
              <p className="text-[14px] leading-[20px] text-gray-600 mb-8">
                Don't have an account?
                <Link
                  to="/register"
                  className="text-[#2283FF] hover:text-[#1a6cd4] ml-1"
                >
                  Sign up
                </Link>
              </p>
            </div>

            {error && !isInvalidMail && !isInvalidPassword && !isOtpError && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
                {error}
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (step === "login") {
                  handleLogin(e);
                } else {
                  handle2FASubmit(e);
                }
              }}
              className="space-y-6"
              noValidate
            >
              <div>
                <label
                  className={`block text-[16px] font-medium ${
                    isInvalidMail || isInvalidPassword
                      ? "text-[#E52F2F]"
                      : "text-black "
                  } mb-2`}
                >
                  Email address
                </label>
                <input
                  type="email"
                  className={`placeholder:text-black/60 placeholder:font-medium placeholder:text-[16px] placeholder:leading-[21px] w-full px-4 py-3 border ${
                    isInvalidMail || isInvalidPassword
                      ? "border-[#E52F2F]"
                      : "border-gray-300"
                  } rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2283FF] focus:border-[#2283FF] text-black font-medium text-[16px] ${
                    step === "2fa" ? "bg-gray-50" : ""
                  } `}
                  placeholder="Email address"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  disabled={step === "2fa"}
                />

                {isInvalidMail && (
                  <div className="mt-2 text-[#E52F2F] text-sm">
                    Email address does not exist.
                  </div>
                )}
              </div>

              <div>
                <div className="flex justify-between items-center mb-1">
                  <label
                    className={`block text-[16px] font-medium ${
                      isInvalidPassword ? "text-[#E52F2F]" : "text-black "
                    } mb-2`}
                  >
                    Password
                  </label>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    className={`placeholder:text-black/60 placeholder:font-medium placeholder:text-[16px] placeholder:leading-[21px] w-full px-4 py-3 border ${
                      isInvalidPassword ? "border-[#E52F2F]" : "border-gray-300"
                    } rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2283FF] focus:border-[#2283FF] pr-12 text-black font-medium text-[16px]`}
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                  />
                  <button
                    type="button"
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-900 hover:text-gray-700"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    )}
                  </button>
                </div>

                {isInvalidPassword && (
                  <div className="mt-2 text-[#E52F2F] text-sm">
                    Email or Password Doesn't Matched
                  </div>
                )}

                <div className="flex mt-3 gap-1 items-center">
                  <p className="text-sm text-black font-medium">
                    Forgot Password?
                  </p>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-[#2283FF] hover:text-[#1a6cd4]"
                  >
                    Reset
                  </Link>
                </div>
              </div>

              {step === "2fa" && (
                <div className="mt-8">
                  <label
                    className={`block text-[16px] font-medium ${
                      isOtpError ? "text-[#FF3B30]" : "text-black"
                    } mb-1`}
                  >
                    Enter Verification code
                  </label>
                  <p className=" text-[#000000]/60 mb-2 mt-2 text-[13px]">
                    We've sent an SMS with an activation code to your phone{" "}
                    <span className="text-black font-medium">
                      {/* {formattedPhoneNumber} */}
                    </span>
                  </p>

                  <div className="flex gap-3">
                    {verificationCode.map((code, index) => (
                      <input
                        key={index}
                        id={`code-${index}`}
                        type="text"
                        placeholder="X"
                        inputMode="numeric"
                        pattern="\d*"
                        autoComplete={index === 0 ? "one-time-code" : "off"}
                        // No maxLength: lets the browser deliver the full pasted
                        // string to onChange so we can distribute across boxes.
                        className={`${
                          isOtpError ? "border-[#FF3B30]" : "border-gray-300"
                        } w-[44px] h-[44px] text-center border rounded-xl focus:outline-none focus:ring-1 focus:ring-[#2283FF] focus:border-[#2283FF] text-lg font-medium`}
                        value={code}
                        onChange={(e) =>
                          handleCodeChange(index, e.target.value)
                        }
                        onPaste={handleCodePaste}
                        onKeyDown={(e) => {
                          if (e.key === "Backspace" && !code && index > 0) {
                            document
                              .getElementById(`code-${index - 1}`)
                              .focus();
                          }
                        }}
                      />
                    ))}
                  </div>

                  {isOtpError && (
                    <div className="text-[#FF3B30] text-[12px] mb-4 mt-2">
                      Oops! That code doesn't seem right. Double-check and try
                      again, or request a new code.
                    </div>
                  )}

                  <div className="mb-4 mt-2">
                    <div className="text-sm text-gray-600 mb-1">
                      Didn't receive a code?
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <button
                        type="button"
                        onClick={handleResendCode}
                        disabled={!!resending}
                        className="text-[#0066FF] hover:underline disabled:opacity-50 disabled:no-underline"
                      >
                        {resending === "sms" ? "Sending SMS..." : "Resend SMS"}
                      </button>
                      <span className="text-gray-300">|</span>
                      <button
                        type="button"
                        onClick={handleResendByEmail}
                        disabled={!!resending}
                        className="text-[#0066FF] hover:underline disabled:opacity-50 disabled:no-underline"
                      >
                        {resending === "email"
                          ? "Sending email..."
                          : "Send by Email"}
                      </button>
                    </div>
                  </div>

                  {/* Trust this device — opt-in. Backend mints a 14-day
                      cookie + DB row; subsequent logins from this browser
                      skip the OTP step entirely. Default OFF so a user
                      on a shared/public device never silently enrols. */}
                  <label className="flex items-start gap-2 mb-4 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={trustDevice}
                      onChange={(e) => setTrustDevice(e.target.checked)}
                      className="mt-1 h-4 w-4 accent-[#2283FF] cursor-pointer"
                    />
                    <span className="text-sm text-gray-700 leading-snug">
                      Trust this device for 14 days
                      <span
                        className="block text-xs text-gray-500 mt-0.5"
                        title="You won't need to verify with OTP from this device for the next 14 days. Use only on your personal devices."
                      >
                        You won't need OTP from this browser for 14 days. Use
                        only on personal devices.
                      </span>
                    </span>
                  </label>
                </div>
              )}

              <button
                type="submit"
                disabled={
                  loading ||
                  (step === "login"
                    ? !formData.email || !formData.password
                    : verificationCode.some((c) => !c))
                }
                className={`w-full py-2 px-4 rounded-lg transition-colors ${
                  loading ||
                  (step === "login"
                    ? !formData.email || !formData.password
                    : verificationCode.some((c) => !c))
                    ? "bg-[#2283FF]/50 text-[#F5F5F5] cursor-not-allowed"
                    : "bg-[#2283FF] hover:bg-[#1a6cd4] text-white"
                }`}
              >
                {loading
                  ? "Please wait..."
                  : step === "login"
                  ? "Send 2FA verification code"
                  : "Verify code"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
